import React from 'react';
import { ProfileFormData } from './CreateProfileWizard';


interface Props {
  formData: ProfileFormData;
}

export default function ProfileStep3({ formData }: Props) {
  const connectedSocials = [
    formData.instagram && { label: 'Instagram', icon: '📸', value: formData.instagram },
    formData.youtube && { label: 'YouTube', icon: '▶', value: formData.youtube },
    formData.twitter && { label: 'Twitter / X', icon: '🐦', value: formData.twitter },
    formData.website && { label: 'Website', icon: '🌐', value: formData.website },
  ].filter(Boolean) as Array<{ label: string; icon: string; value: string }>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-700 text-foreground mb-1">Review & Publish</h2>
        <p className="text-sm text-muted-foreground">
          Everything looks good? Click Publish to make your bio page live.
        </p>
      </div>

      {/* Profile summary card */}
      <div className="bg-muted/50 rounded-xl p-5 border border-border space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-brand flex items-center justify-center text-white font-800 text-xl flex-shrink-0">
            {formData.avatarInitials || formData.displayName?.slice(0, 2).toUpperCase() || 'U'}
          </div>
          <div>
            <p className="font-700 text-foreground text-lg">{formData.displayName || '—'}</p>
            <p className="text-sm text-primary font-500">biolink.app/{formData.username || '—'}</p>
            {formData.location && (
              <p className="text-xs text-muted-foreground mt-0.5">📍 {formData.location}</p>
            )}
          </div>
        </div>

        {formData.bio && (
          <div className="border-t border-border pt-4">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-2">Bio</p>
            <p className="text-sm text-foreground whitespace-pre-line">{formData.bio}</p>
          </div>
        )}

        {connectedSocials.length > 0 && (
          <div className="border-t border-border pt-4">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-3">Connected Socials</p>
            <div className="flex flex-wrap gap-2">
              {connectedSocials.map((social) => (
                <span
                  key={`review-social-${social.label}`}
                  className="flex items-center gap-1.5 text-xs font-600 px-3 py-1.5 rounded-full bg-card border border-border text-foreground"
                >
                  {social.icon} {social.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Checklist */}
      <div className="space-y-2.5">
        {[
          { label: 'Username set', done: !!formData.username, key: 'check-username' },
          { label: 'Display name added', done: !!formData.displayName, key: 'check-name' },
          { label: 'Bio written', done: !!formData.bio, key: 'check-bio' },
          { label: 'At least one social connected', done: connectedSocials.length > 0, key: 'check-social' },
        ].map((item) => (
          <div key={item.key} className="flex items-center gap-3">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
              item.done ? 'bg-success' : 'bg-border'
            }`}>
              {item.done ? (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3 h-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <span className={`text-sm ${item.done ? 'text-foreground font-500' : 'text-muted-foreground'}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>

      <div className="bg-success/5 border border-success/20 rounded-xl p-4">
        <p className="text-sm font-600 text-success mb-1">🚀 Ready to publish!</p>
        <p className="text-xs text-muted-foreground">
          Your page will be live at{' '}
          <span className="font-600 text-primary">biolink.app/{formData.username}</span>
          {' '}immediately after publishing.
        </p>
      </div>
    </div>
  );
}